import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { imgSrc, inr } from "@/lib/api";
import { useStore } from "@/context/StoreContext";

export default function ProductCard({ product, aspect = "aspect-[4/5]", className = "", delay = 0 }) {
  const { addItem } = useStore();
  const soldOut = (product.stock || 0) <= 0;
  const onSale = product.compare_at_price > product.price;

  return (
    <motion.article
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
      className={`group flex flex-col ${className}`}
      data-testid={`product-card-${product.slug}`}
    >
      <div className={`relative overflow-hidden border border-line bg-white ${aspect}`}>
        <Link
          to={`/product/${product.slug}`}
          className="absolute inset-0 z-10"
          aria-label={product.name}
          data-testid={`product-link-${product.slug}`}
        />
        <img
          src={imgSrc(product.images?.[0])}
          alt={product.name}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
        />
        {onSale && (
          <span className="absolute left-0 top-4 z-10 bg-ink px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-parchment">
            Save {inr(product.compare_at_price - product.price)}
          </span>
        )}
        {soldOut && (
          <span className="absolute right-0 top-4 z-10 bg-white px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-ink">
            Sold out
          </span>
        )}
        <button
          onClick={() => addItem(product)}
          disabled={soldOut}
          data-testid={`quick-add-${product.slug}`}
          className="absolute inset-x-4 bottom-4 z-20 translate-y-3 rounded-full bg-ink/90 py-3 text-xs font-semibold uppercase tracking-[0.18em] text-parchment opacity-0 backdrop-blur transition-all duration-300 hover:bg-terracotta group-hover:translate-y-0 group-hover:opacity-100 disabled:cursor-not-allowed disabled:bg-stone-400"
        >
          {soldOut ? "Sold out" : "Add to cart"}
        </button>
      </div>
      <div className="flex items-start justify-between gap-3 pt-4">
        <div>
          <p className="label-mono">{product.category}</p>
          <Link
            to={`/product/${product.slug}`}
            className="mt-1 block font-display text-xl leading-snug transition-colors duration-200 hover:text-terracotta"
          >
            {product.name}
          </Link>
        </div>
        <p className="whitespace-nowrap pt-4 text-sm font-semibold">{inr(product.price)}</p>
      </div>
    </motion.article>
  );
}
